import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { t, locale } from "@/i18n";

export const QUOTA_KEYS = ["menu_analyses", "invoices", "ai_actions", "recipes", "ingredients"];
export const MONTHLY_KEYS = ["menu_analyses", "invoices", "ai_actions"];
export const quotaLabel = (k, n) => t(n === 1 ? `quota.label1_${k}` : `quota.label_${k}`) + (MONTHLY_KEYS.includes(k) ? t("plans.per_month_short") : "");
export const planName = (p) => t(`plans.name_${p}`);
const price = (v) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(v);

function PlanCard({ id, plan, current, currentInterval, trial, yearly, loggedIn, busy, onChoose }) {
  const rec = id === "pro";
  const isCurrent = current === id && !trial && (id === "free" || (currentInterval || "month") === (yearly ? "year" : "month"));
  const freeLocked = id === "free" && loggedIn && !isCurrent;
  return (
    <div className={`relative flex flex-col rounded-2xl border bg-card p-6 ${rec ? "border-primary shadow-lg ring-1 ring-primary" : ""}`} data-testid={`plan-card-${id}`}>
      {rec && <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground" data-testid="plan-recommended-badge">{t("plans.recommended")}</span>}
      <h3 className="text-lg font-semibold">{planName(id)}</h3>
      <p className="mt-1 min-h-[2.5rem] text-sm text-muted-foreground">{t(`plans.tagline_${id}`)}</p>
      <p className="mt-4"><span className="num text-3xl font-bold" data-testid={`plan-price-${id}`}>{price(yearly ? plan.annual_price : plan.price)}</span><span className="text-sm text-muted-foreground">{t(yearly ? "plans.per_year" : "plans.per_month")}</span></p>
      <p className="mt-1 min-h-[1.25rem] text-xs text-muted-foreground" data-testid={`plan-annual-note-${id}`}>
        {yearly && plan.annual_price > 0 && t("plans.annual_equiv", { price: price(plan.annual_price / 12), saved: price(plan.price * 12 - plan.annual_price) })}
      </p>
      <ul className="mt-5 flex-1 space-y-2 text-sm">
        {QUOTA_KEYS.map((k) => (
          <li key={k} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span><span className="num font-semibold">{plan.limits[k] ?? "∞"}</span> {quotaLabel(k, plan.limits[k])}</span></li>
        ))}
      </ul>
      <Button className="mt-6 w-full" variant={rec ? "default" : "outline"} disabled={isCurrent || freeLocked || busy === id} onClick={() => onChoose(id)} data-testid={`plan-choose-${id}`}>
        {busy === id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {isCurrent ? t("plans.current") : freeLocked ? t("plans.free_fallback") : current === id ? t("plans.keep", { name: planName(id) }) : id === "free" ? t("plans.start_free") : t("plans.choose", { name: planName(id) })}
      </Button>
      {id === "free" && !loggedIn && <p className="mt-2 text-center text-xs text-muted-foreground">{t("plans.no_card")}</p>}
    </div>
  );
}

function IntervalToggle({ yearly, setYearly }) {
  const btn = (on, label, id) => (
    <button type="button" onClick={() => setYearly(id === "year")} data-testid={`interval-${id}`}
      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
  );
  return (
    <div className="mb-8 inline-flex items-center gap-1 rounded-full border bg-card p-1" data-testid="interval-toggle">
      {btn(!yearly, t("plans.monthly"), "month")}
      {btn(yearly, <>{t("plans.yearly")} <span className={`ml-1 rounded-full px-2 py-0.5 text-xs ${yearly ? "bg-primary-foreground/15 text-primary-foreground" : "bg-primary/15 text-primary"}`} data-testid="interval-badge">{t("plans.two_months_free")}</span></>, "year")}
    </div>
  );
}

export function PlanCards({ current, currentInterval, trial }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [yearly, setYearly] = useState(false);
  useEffect(() => { api.get("/plans").then((r) => setData(r.data)).catch(() => {}); }, []);

  const choose = async (id) => {
    if (!user) return nav("/register");
    if (id === "free") return nav("/dashboard");
    setBusy(id);
    try {
      const { data: s } = await api.post("/billing/checkout", { plan: id, interval: yearly ? "year" : "month", origin_url: window.location.origin });
      window.location.href = s.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  return (
    <>
      <IntervalToggle yearly={yearly} setYearly={setYearly} />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="plan-cards">
        {data.order.map((id) => <PlanCard key={id} id={id} plan={data.plans[id]} current={current} currentInterval={currentInterval} trial={trial} yearly={yearly} loggedIn={!!user} busy={busy} onChoose={choose} />)}
      </div>
    </>
  );
}
